import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { CircleAlert, Clock, Eye, EyeOff, Inbox, Mail } from 'lucide-react';

import { emailRule } from './authRules';

/**
 * The bodies of the sign-in window (AuthDialog): sign in, sign up, forgot password and the two "check your email"
 * steps. Presentational: AuthDialog owns the requests, the busy state and the server's answer (`alert`, shown right
 * above the button, and `badField`, the field it marks red). `copy(key)` reads components.AuthDialog.*.
 */

export const Field = ({ id, label, error, hint, children }) => {
  const descriptionId = error || hint ? `${id}-desc` : undefined;
  return (
    <div className="mt-ad-field">
      <label className="mt-ad-label" htmlFor={id}>
        {label}
      </label>
      {children(descriptionId)}
      {error ? (
        <span className="mt-ad-error" id={descriptionId}>
          <CircleAlert aria-hidden="true" />
          {error}
        </span>
      ) : hint ? (
        <span className="mt-ad-hint" id={descriptionId}>
          {hint}
        </span>
      ) : null}
    </div>
  );
};

export const PasswordInput = ({ id, registration, autoComplete, invalid, describedBy, busy, t, autoFocus }) => {
  const [isVisible, setIsVisible] = useState(false);
  return (
    <span className="mt-ad-control mt-ad-control--reveal">
      <input
        {...registration}
        id={id}
        className="mt-ad-input"
        type={isVisible ? 'text' : 'password'}
        autoComplete={autoComplete}
        autoCapitalize="none"
        spellCheck={false}
        autoFocus={autoFocus}
        readOnly={busy}
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy}
      />
      <button
        type="button"
        className="mt-ad-reveal"
        onClick={() => setIsVisible(!isVisible)}
        aria-label={isVisible ? t('standalone.hidePassword') : t('standalone.showPassword')}
        aria-controls={id}
      >
        {isVisible ? <EyeOff aria-hidden="true" /> : <Eye aria-hidden="true" />}
      </button>
    </span>
  );
};

export const SubmitButton = ({ busy, label, busyLabel, disabled, type = 'submit', onClick }) => (
  <button type={type} className="mt-ad-go" onClick={onClick} disabled={disabled} aria-busy={busy || undefined}>
    {busy && <span className="mt-btn__spinner" aria-hidden="true" />}
    {busy ? busyLabel : label}
  </button>
);

const Foot = ({ text, action, onClick, disabled }) => (
  <p className="mt-ad-foot">
    {text}{' '}
    <button type="button" className="mt-ad-link" onClick={onClick} disabled={disabled}>
      {action}
    </button>
  </p>
);

/**
 * The server's answer, inside the form right above its button. Errors are danger; the unconfirmed-email and
 * session notices use the one notice panel.
 */
export const ServerAlert = ({ kind, action, copy, email, onForgot, onLogin, resend }) => {
  if (!kind) return null;
  if (kind === 'unconfirmed') {
    return (
      <div className="mt-ad-alert mt-ad-alert--notice" role="alert">
        <Mail aria-hidden="true" />
        <div>
          <p>
            {copy('errors.unconfirmedBefore')} <strong>{email}</strong>
            {copy('errors.unconfirmedAfter')}
          </p>
          {resend && (
            <div className="mt-ad-alert__actions">
              <button type="button" className="mt-ad-link" onClick={resend.onClick} disabled={resend.cooldown > 0}>
                {resend.cooldown > 0 ? copy('resendIn', { seconds: resend.cooldown }) : copy('errors.resendConfirmation')}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }
  const message =
    kind === 'wrong' ? (
      <>
        <strong>{copy('errors.wrongTitle')}</strong> {copy('errors.wrongText')}
      </>
    ) : kind === 'taken' ? (
      <>
        <strong>{copy('errors.takenTitle')}</strong> {copy('errors.takenText')}
      </>
    ) : kind === 'turnstile' ? (
      copy('errors.turnstile')
    ) : kind === 'network' ? (
      copy('errors.network')
    ) : (
      copy(`errors.server.${action}`)
    );
  return (
    <div className="mt-ad-alert mt-ad-alert--danger" role="alert">
      <CircleAlert aria-hidden="true" />
      <div>
        <p>{message}</p>
        {kind === 'wrong' && (
          <div className="mt-ad-alert__actions">
            <button type="button" className="mt-ad-link" onClick={onForgot}>
              {copy('errors.newPassword')}
            </button>
          </div>
        )}
        {kind === 'taken' && (
          <div className="mt-ad-alert__actions">
            <button type="button" className="mt-ad-link" onClick={onLogin}>
              {copy('errors.loginWithEmail')}
            </button>
            <button type="button" className="mt-ad-link" onClick={onForgot}>
              {copy('login.forgot')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export const LoginForm = ({ copy, t, busy, badField, defaultEmail, autoFocus, notice, alert, turnstile, foot, onSubmit, onEmailChange, onFieldEdit, onForgot, onSignup }) => {
  const schema = useMemo(
    () =>
      yup.object({
        identifier: emailRule(copy),
        password: yup.string().required(copy('validation.passwordRequired')).min(6, copy('validation.passwordShortLogin')),
      }),
    [copy]
  );
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({ resolver: yupResolver(schema), defaultValues: { identifier: defaultEmail, password: '' } });
  const identifier = register('identifier', {
    onChange: (event) => {
      onEmailChange(event.target.value);
      onFieldEdit('email');
    },
  });
  const password = register('password', { onChange: () => onFieldEdit('password') });

  return (
    <form className="mt-ad-form" noValidate onSubmit={handleSubmit(onSubmit)}>
      {notice}
      <Field id="mt-ad-login-email" label={copy('fields.email')} error={errors.identifier?.message}>
        {(describedBy) => (
          <input
            {...identifier}
            id="mt-ad-login-email"
            className="mt-ad-input"
            type="email"
            inputMode="email"
            autoComplete="username"
            autoCapitalize="none"
            spellCheck={false}
            autoFocus={autoFocus}
            readOnly={!!busy}
            aria-invalid={!!errors.identifier || badField === 'email' || undefined}
            aria-describedby={describedBy}
          />
        )}
      </Field>
      <Field id="mt-ad-login-password" label={copy('fields.password')} error={errors.password?.message}>
        {(describedBy) => (
          <PasswordInput
            id="mt-ad-login-password"
            registration={password}
            autoComplete="current-password"
            invalid={!!errors.password || badField === 'password'}
            describedBy={describedBy}
            busy={!!busy}
            t={t}
          />
        )}
      </Field>
      <div className="mt-ad-row-end">
        <button type="button" className="mt-ad-link" onClick={onForgot} disabled={!!busy}>
          {copy('login.forgot')}
        </button>
      </div>
      {turnstile}
      {alert}
      <SubmitButton busy={!!busy} label={copy('login.submit')} busyLabel={copy(busy === 'verify' ? 'busy.verify' : 'busy.login')} />
      {foot && <Foot text={copy('login.noAccount')} action={copy('login.toSignup')} onClick={onSignup} disabled={!!busy} />}
    </form>
  );
};

export const SignupForm = ({ copy, t, busy, badField, defaultEmail, autoFocus, alert, turnstile, foot, onSubmit, onEmailChange, onFieldEdit, onLogin }) => {
  const schema = useMemo(
    () =>
      yup.object({
        name: yup.string().trim().required(copy('validation.nameRequired')),
        email: emailRule(copy),
        password: yup.string().required(copy('validation.passwordNew')).min(6, copy('validation.passwordShort')),
        // Typed twice, so a typo can't become the account's password
        passwordConfirm: yup
          .string()
          .required(copy('validation.passwordConfirmRequired'))
          .oneOf([yup.ref('password')], copy('validation.passwordMismatch')),
        terms: yup.boolean().oneOf([true], copy('validation.terms')),
      }),
    [copy]
  );
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({ resolver: yupResolver(schema), defaultValues: { name: '', email: defaultEmail, password: '', passwordConfirm: '', terms: false } });
  const email = register('email', {
    onChange: (event) => {
      onEmailChange(event.target.value);
      onFieldEdit('email');
    },
  });

  return (
    <form className="mt-ad-form" noValidate onSubmit={handleSubmit(onSubmit)}>
      <Field id="mt-ad-signup-name" label={copy('fields.name')} error={errors.name?.message}>
        {(describedBy) => (
          <input
            {...register('name')}
            id="mt-ad-signup-name"
            className="mt-ad-input"
            autoComplete="name"
            autoFocus={autoFocus}
            readOnly={!!busy}
            aria-invalid={!!errors.name || undefined}
            aria-describedby={describedBy}
          />
        )}
      </Field>
      <Field id="mt-ad-signup-email" label={copy('fields.email')} error={errors.email?.message} hint={copy('signup.emailHint')}>
        {(describedBy) => (
          <input
            {...email}
            id="mt-ad-signup-email"
            className="mt-ad-input"
            type="email"
            inputMode="email"
            autoComplete="username"
            autoCapitalize="none"
            spellCheck={false}
            readOnly={!!busy}
            aria-invalid={!!errors.email || badField === 'email' || undefined}
            aria-describedby={describedBy}
          />
        )}
      </Field>
      <Field id="mt-ad-signup-password" label={copy('fields.password')} error={errors.password?.message} hint={copy('signup.passwordHint')}>
        {(describedBy) => (
          <PasswordInput
            id="mt-ad-signup-password"
            registration={register('password')}
            autoComplete="new-password"
            invalid={!!errors.password}
            describedBy={describedBy}
            busy={!!busy}
            t={t}
          />
        )}
      </Field>
      <Field id="mt-ad-signup-password-confirm" label={copy('fields.passwordConfirm')} error={errors.passwordConfirm?.message}>
        {(describedBy) => (
          <PasswordInput
            id="mt-ad-signup-password-confirm"
            registration={register('passwordConfirm')}
            autoComplete="new-password"
            invalid={!!errors.passwordConfirm}
            describedBy={describedBy}
            busy={!!busy}
            t={t}
          />
        )}
      </Field>
      <div className="mt-ad-field">
        <label className={`mt-ad-terms${errors.terms ? ' mt-ad-terms--invalid' : ''}`}>
          <input
            {...register('terms')}
            type="checkbox"
            disabled={!!busy}
            aria-invalid={!!errors.terms || undefined}
            aria-describedby={errors.terms ? 'mt-ad-signup-terms-desc' : undefined}
          />
          <span>
            {t('standalone.agreeTerms')}{' '}
            <a href={t('termsUrl')} target="_blank" rel="noopener noreferrer">
              {t('standalone.agreeTermsLinkText')}
            </a>{' '}
            {t('standalone.agreePrivacy')}{' '}
            <a href={t('privacyUrl')} target="_blank" rel="noopener noreferrer">
              {t('standalone.privacyLinkText')}
            </a>
          </span>
        </label>
        {errors.terms && (
          <span className="mt-ad-error" id="mt-ad-signup-terms-desc">
            <CircleAlert aria-hidden="true" />
            {errors.terms.message}
          </span>
        )}
      </div>
      {turnstile}
      {alert}
      <SubmitButton busy={!!busy} label={copy('signup.submit')} busyLabel={copy(busy === 'verify' ? 'busy.verify' : 'busy.signup')} />
      {foot && <Foot text={copy('signup.hasAccount')} action={copy('signup.toLogin')} onClick={onLogin} disabled={!!busy} />}
    </form>
  );
};

export const ForgotForm = ({ copy, busy, defaultEmail, autoFocus, cooldown, alert, onSubmit, onEmailChange, onLogin }) => {
  const schema = useMemo(() => yup.object({ email: emailRule(copy) }), [copy]);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({ resolver: yupResolver(schema), defaultValues: { email: defaultEmail } });
  const email = register('email', { onChange: (event) => onEmailChange(event.target.value) });

  return (
    <form className="mt-ad-form" noValidate onSubmit={handleSubmit(onSubmit)}>
      <Field id="mt-ad-forgot-email" label={copy('fields.email')} error={errors.email?.message}>
        {(describedBy) => (
          <input
            {...email}
            id="mt-ad-forgot-email"
            className="mt-ad-input"
            type="email"
            inputMode="email"
            autoComplete="username"
            autoCapitalize="none"
            spellCheck={false}
            autoFocus={autoFocus}
            readOnly={!!busy}
            aria-invalid={!!errors.email || undefined}
            aria-describedby={describedBy}
          />
        )}
      </Field>
      {alert}
      <SubmitButton
        busy={!!busy}
        disabled={cooldown > 0}
        label={cooldown > 0 ? copy('resendIn', { seconds: cooldown }) : copy('forgot.submit')}
        busyLabel={copy('busy.send')}
      />
      <Foot text={copy('forgot.remembered')} action={copy('forgot.toLogin')} onClick={onLogin} disabled={!!busy} />
    </form>
  );
};

// After asking for a new password: the same answer whether or not the email has an account
export const ForgotSent = ({ copy, busy, email, cooldown, alert, onLogin, onResend, onChangeEmail }) => (
  <div className="mt-ad-form">
    <p className="mt-ad-sent">
      {copy('forgotSent.textBefore')} <strong>{email}</strong>
      {copy('forgotSent.textAfter')}
    </p>
    <ul className="mt-ad-tips">
      <li>
        <Inbox aria-hidden="true" />
        <span>{copy('forgotSent.spam')}</span>
      </li>
      <li>
        <Clock aria-hidden="true" />
        <span>{cooldown > 0 ? copy('forgotSent.waitIn', { seconds: cooldown }) : copy('forgotSent.canResend')}</span>
      </li>
    </ul>
    {alert}
    <SubmitButton type="button" label={copy('backToLogin')} onClick={onLogin} disabled={!!busy} />
    <button type="button" className="mt-ad-quiet" onClick={onResend} disabled={!!busy || cooldown > 0} aria-busy={busy || undefined}>
      {busy && <span className="mt-btn__spinner" aria-hidden="true" />}
      {busy ? copy('busy.send') : cooldown > 0 ? copy('resendIn', { seconds: cooldown }) : copy('forgotSent.resend')}
    </button>
    <Foot text={copy('forgotSent.wrongEmail')} action={copy('forgotSent.changeEmail')} onClick={onChangeEmail} disabled={!!busy} />
  </div>
);

// A sign-up that waits on the email confirmation (only if the API asks for it again: today it signs in right away)
export const SignupSent = ({ copy, email, cooldown, alert, onLogin, onResend }) => (
  <div className="mt-ad-form">
    <p className="mt-ad-sent">
      {copy('signupSent.textBefore')} <strong>{email}</strong>
      {copy('signupSent.textAfter')}
    </p>
    {alert}
    <SubmitButton type="button" label={copy('signupSent.toLogin')} onClick={onLogin} />
    <Foot
      text={copy('signupSent.missing')}
      action={cooldown > 0 ? copy('signupSent.resendIn', { seconds: cooldown }) : copy('signupSent.resend')}
      onClick={onResend}
      disabled={cooldown > 0}
    />
  </div>
);
